import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate, formatTime } from '../../utils/format';
import { peakOf, percent } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge
} from '../../components/ui';
import { attendanceSummary } from './analytics';
import type { AttendanceLike } from './analytics';

export interface AttendanceSummaryProps {
  records: AttendanceLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

/** The Attendance module's Summary tab: tiles, then report panels and watch-lists. */
const AttendanceSummaryView = ({ records, loading, isDarkMode }: AttendanceSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => attendanceSummary(records), [records]);
  const mixTotal = s.mix.reduce((n, m) => n + m.count, 0);
  const monthPeak = peakOf(s.months, (r) => r.average);
  const eventPeak = peakOf(s.byEvent, (r) => r.average);
  const bestPeak = peakOf(s.best, (r) => Number(r.attendees) || 0);
  const attention = s.issues.length + s.missedSundays.length;
  const mixColor = (name: string) =>
    ({ Men: t.primary, Women: t.info, Youth: t.income, Children: t.warning } as Record<string, string>)[name] || t.textFaint;

  const serviceColumns = (color: string) => [
    { key: 'date', header: 'Date', nowrap: true, render: (r: AttendanceLike) => <strong>{formatDate(r.date)}</strong> },
    { key: 'serviceTime', header: 'Time', nowrap: true, render: (r: AttendanceLike) => formatTime(r.serviceTime) },
    { key: 'eventName', header: 'Service', render: (r: AttendanceLike) => r.eventName || '-' },
    { key: 'bar', header: '', render: (r: AttendanceLike) => <BarCell value={Number(r.attendees) || 0} max={bestPeak} color={color} isDarkMode={isDarkMode} /> },
    { key: 'attendees', header: 'Total', align: 'right' as const, render: (r: AttendanceLike) => <strong>{String(r.attendees ?? '-')}</strong> }
  ];

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Services Recorded" value={String(s.services)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Total Attendance" value={String(s.totalAttendance)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Avg. / Service" value={s.average === null ? '—' : String(s.average)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Avg. Sunday (12 wks)" value={s.sundayAverage === null ? '—' : String(s.sundayAverage)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Highest Turnout" value={s.highest ? String(s.highest.attendees) : '—'} color={t.warning} isDarkMode={isDarkMode} />
        <KpiCard label="Services (30 d)" value={String(s.services30)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Avg. (30 d)" value={s.average30 === null ? '—' : String(s.average30)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="New Visitors (Year)" value={String(s.visitorsYear)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="New Visitors (30 d)" value={String(s.visitors30)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Needs Attention" value={String(attention)} color={attention ? t.danger : t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Congregation Mix"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Group', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.count, mixTotal)} max={100} showPercent color={mixColor(r.name)} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Attendance', align: 'right' }
          ]}
          rows={mixTotal ? s.mix : []}
          rowKey="name"
          emptyMessage="No attendance recorded yet."
          below={mixTotal ? <TotalRow label="Total attendance" value={s.totalAttendance} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="By Service / Event"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Service', render: (r) => <strong>{r.name}</strong> },
            { key: 'services', header: 'Held', align: 'right' },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.average} max={eventPeak} color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'average', header: 'Avg.', align: 'right' },
            { key: 'total', header: 'Total', align: 'right' }
          ]}
          rows={s.byEvent}
          rowKey="name"
          emptyMessage="No services counted yet."
        />
      </SummaryColumns>

      <TablePanel
        title="Attendance by Month — Last 12 Months"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="480px"
        columns={[
          { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
          { key: 'services', header: 'Services', align: 'right' },
          { key: 'bar', header: 'Avg. per service', render: (r) => <BarCell value={r.average} max={monthPeak} color={t.info} isDarkMode={isDarkMode} /> },
          { key: 'average', header: 'Avg.', align: 'right', render: (r) => (r.services ? r.average : '—') },
          { key: 'total', header: 'Total', align: 'right' }
        ]}
        rows={s.months}
        rowKey="month"
        emptyMessage="No attendance recorded yet."
        below={<TotalRow label="Attendance in 12 months" value={s.months.reduce((n, m) => n + m.total, 0)} isDarkMode={isDarkMode} />}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Best Attended — Last 12 Months"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={serviceColumns(t.income)}
          rows={s.best}
          rowKey={(r) => `${r.recordId}|${r.date}|${r.serviceTime}`}
          emptyMessage="No services counted in the last 12 months."
        />
        <TablePanel
          title="Lowest Attended — Last 12 Months"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={serviceColumns(t.warning)}
          rows={s.worst}
          rowKey={(r) => `${r.recordId}|${r.date}|${r.serviceTime}`}
          emptyMessage="Not enough services yet to compare."
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Counts to Check"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'date', header: 'Date', nowrap: true, render: (r) => <strong>{formatDate(r.record.date)}</strong> },
            { key: 'service', header: 'Service', render: (r) => r.record.eventName || '-' },
            { key: 'reason', header: 'Why', wrap: true, render: (r) => <span style={{ color: t.warning }}>{r.reason}</span> }
          ]}
          rows={s.issues}
          rowKey={(r) => `${r.record.recordId}|${r.record.date}`}
          emptyMessage="Every service has a headcount that matches its breakdown."
        />
        <TablePanel
          title="Sundays Not Recorded — Last 8 Weeks"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'date', header: 'Sunday', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
            { key: 'status', header: '', render: () => <Badge tone="warning">No record</Badge> }
          ]}
          rows={s.missedSundays.map((date) => ({ date }))}
          rowKey="date"
          emptyMessage="Every Sunday of the last 8 weeks has a record."
        />
      </SummaryColumns>
    </>
  );
};

export default AttendanceSummaryView;
